import { useCallback, useMemo } from 'react';
import { To, useLocation, useNavigate } from 'react-router-dom';
import { logError } from 'shared/helpers/Logger';
import { useCallbackRef } from 'shared/hooks/useCallbackRef';
import {
  LocationParamsOptions,
  UpdateParams,
} from 'shared/routing/LocationParams';
import { parseSearchQuery, stringifySearchQuery } from 'shared/utils/URLUtils';
import { JsonObject } from 'type-fest';

// for compatibility (parse, stringify from 'qs') (RoutingHelpers.ts, UrlUtils.ts) after the transfer - delete
export function useLocationParamsLegacy<T extends JsonObject>({
  yupSchema,
}: LocationParamsOptions<T> = {}): readonly [T, UpdateParams<T>] {
  const parseParams = useCallback(
    (search: string): T => {
      let nextParams = parseSearchQuery(search) as unknown as T;

      if (yupSchema) {
        try {
          nextParams = yupSchema.cast(nextParams);
        } catch (e: unknown) {
          logError(e, 'LocationParamsLegacy');
        }
      }

      return nextParams;
    },
    [yupSchema],
  );

  const location = useLocation();
  const params = useMemo(
    () => parseParams(location.search),
    [parseParams, location.search],
  );

  const navigate = useNavigate();
  const updateParams = useCallback<UpdateParams<T>>(
    (input, { strategy = 'merge', navigation = 'push' } = {}) => {
      const prevParams = parseParams(location.search);
      if (typeof input == 'function') input = input(prevParams);
      if (strategy === 'merge') input = { ...prevParams, ...input };
      const nextLocation: To = { search: stringifySearchQuery(input) };
      const shouldReplace = navigation === 'replace';
      navigate(nextLocation, { replace: shouldReplace });
    },
    [navigate, location.search, parseParams],
  );

  const updateParamsCallback = useCallbackRef(updateParams);

  return [params, updateParamsCallback];
}
